import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { siteImageUrl, clearSiteContentCache } from "../lib/siteContent";
import { AdminNav } from "../components/AdminNav";
import { Loader2, Save, Upload, ImageIcon, ExternalLink, Mail } from "lucide-react";

const LS_TOKEN = "bnm-admin-token";

// Field metadata mirrors SITE_CONTENT_DEFAULT on the backend.
const SECTIONS = [
  {
    key: "hero",
    label: "Hero",
    hint: "The first thing readers see on the landing experience.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "input" },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
    ],
  },
  {
    key: "about",
    label: "About the Author",
    hint: "Casey's introduction and portrait.",
    fields: [
      { key: "role", label: "Role line", type: "input" },
      { key: "deck", label: "Deck / description", type: "textarea" },
      { key: "portrait", label: "Portrait image", type: "image" },
    ],
  },
  {
    key: "ecosystem",
    label: "Ecosystem",
    hint: "The “four doors into the work” section.",
    fields: [
      { key: "heading", label: "Heading", type: "input" },
      { key: "intro", label: "Intro", type: "textarea" },
    ],
  },
  {
    key: "offerings",
    label: "Offerings",
    hint: "Lead-in copy for the offerings section.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "input" },
      { key: "heading", label: "Heading", type: "textarea" },
    ],
  },
  {
    key: "veil",
    label: "Entry Veil",
    hint: "The opening screen before readers enter the book.",
    fields: [
      { key: "entry_prompt", label: "Name/email prompt", type: "input" },
      { key: "free_note", label: "Free-chapter note", type: "input" },
    ],
  },
  {
    key: "vault",
    label: "Language Vault Door",
    hint: "The signup door shown to non-members at the Vault.",
    fields: [
      { key: "gate_heading", label: "Door heading", type: "input" },
      { key: "gate_body", label: "Door message", type: "textarea" },
      { key: "gate_inside", label: "“What's inside” line", type: "textarea" },
    ],
  },
  {
    key: "gallery",
    label: "Gallery Preview",
    hint: "Intro copy on the public gallery preview.",
    fields: [
      { key: "intro", label: "Intro", type: "textarea" },
    ],
  },
  {
    key: "teaser",
    label: "Gallery Teaser",
    hint: "The “Echoes of the Form” invitation shown at reader pauses.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "input" },
      { key: "body", label: "Body", type: "textarea" },
      { key: "cta_label", label: "Button label", type: "input" },
    ],
  },
];

export default function AdminEditor() {
  const [token] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [content, setContent] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingSec, setSavingSec] = useState("");
  const [savedSec, setSavedSec] = useState("");
  const [uploadingField, setUploadingField] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const fileRefs = useRef({});
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { setErr("Please sign in at /admin first."); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/site-content`, auth);
        setContent(data);
      } catch (e) {
        setErr(e.response?.status === 401 ? "Please sign in at /admin first." : "Could not load site content.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const setField = (sec, field, val) =>
    setContent((c) => ({ ...c, [sec]: { ...(c[sec] || {}), [field]: val } }));

  const saveSection = async (sec) => {
    setSavingSec(sec);
    try {
      const { data } = await axios.put(`${API}/admin/site-content`,
        { sections: { [sec]: content[sec] } }, auth);
      setContent(data);
      clearSiteContentCache();
      setErr("");
      setSavedSec(sec);
      setTimeout(() => setSavedSec(""), 1800);
    } catch (e) {
      setErr("Could not save. Try again.");
    } finally { setSavingSec(""); }
  };

  const uploadImage = async (sec, field, file) => {
    if (!file) return;
    setUploadingField(`${sec}.${field}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/admin/site-content/image?section=${sec}&field=${field}`,
        fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      setField(sec, field, data.url);
      clearSiteContentCache();
      setErr("");
    } catch (e) {
      setErr("Image upload failed. Use a JPG, PNG or WEBP.");
    } finally { setUploadingField(""); }
  };

  if (err && !content) return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-ivory-100 px-5 text-center">
      <div>
        <p className="text-ink-soft" data-testid="editor-error">{err}</p>
        <Link to="/admin" className="mt-3 inline-block text-gold-deep underline">Go to /admin</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="admin-editor">
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8">
        <AdminNav />
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl text-ink">Website Editor</h1>
            <p className="mt-1 text-sm text-ink-muted">Edit the marketing copy and imagery shown on the public site.</p>
          </div>
          <a href="/" target="_blank" rel="noreferrer" data-testid="editor-view-site"
            className="inline-flex items-center gap-1.5 rounded-full border border-ivory-400/70 bg-ivory-50 px-4 py-2 font-display text-sm text-ink-soft hover:text-ink">
            <ExternalLink className="h-4 w-4" /> View site
          </a>
        </div>

        {err && content && <p className="mt-4 rounded-lg bg-gold/10 px-4 py-2 text-sm text-gold-deep" data-testid="editor-inline-error">{err}</p>}

        {loading ? (
          <p className="mt-8 flex items-center gap-2 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading content…</p>
        ) : (
          <div className="mt-6 space-y-5">
            {SECTIONS.map((sec) => (
              <section key={sec.key} data-testid={`editor-section-${sec.key}`}
                className="rounded-2xl bg-ivory-50 p-5 shadow-sm ring-1 ring-ivory-400/60 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="font-display text-xl text-ink">{sec.label}</h2>
                    <p className="text-xs text-ink-muted">{sec.hint}</p>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {sec.fields.map((f) => {
                    const val = content?.[sec.key]?.[f.key] || "";
                    if (f.type === "image") {
                      const uploading = uploadingField === `${sec.key}.${f.key}`;
                      return (
                        <div key={f.key}>
                          <p className="mb-1.5 text-[11px] uppercase tracking-[0.14em] text-ink-muted">{f.label}</p>
                          <div className="flex items-center gap-4">
                            <div className="flex h-20 w-20 flex-none items-center justify-center overflow-hidden rounded-xl bg-ivory-200 ring-1 ring-ivory-400/60">
                              {val ? (
                                <img src={siteImageUrl(val)} alt={f.label} className="h-full w-full object-cover" data-testid={`editor-img-${sec.key}-${f.key}`} />
                              ) : (
                                <ImageIcon className="h-6 w-6 text-ink-muted" />
                              )}
                            </div>
                            <div>
                              <input ref={(el) => (fileRefs.current[`${sec.key}.${f.key}`] = el)} type="file"
                                accept="image/jpeg,image/png,image/webp" className="hidden"
                                data-testid={`editor-file-${sec.key}-${f.key}`}
                                onChange={(e) => uploadImage(sec.key, f.key, e.target.files?.[0])} />
                              <button type="button" disabled={uploading}
                                onClick={() => fileRefs.current[`${sec.key}.${f.key}`]?.click()}
                                data-testid={`editor-upload-${sec.key}-${f.key}`}
                                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                              </button>
                              <p className="mt-1 text-[11px] text-ink-muted">JPG, PNG or WEBP. Saved immediately.</p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={f.key}>
                        <p className="mb-1.5 text-[11px] uppercase tracking-[0.14em] text-ink-muted">{f.label}</p>
                        {f.type === "textarea" ? (
                          <textarea rows={3} value={val} onChange={(e) => setField(sec.key, f.key, e.target.value)}
                            data-testid={`editor-input-${sec.key}-${f.key}`}
                            className="w-full resize-y rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                        ) : (
                          <input type="text" value={val} onChange={(e) => setField(sec.key, f.key, e.target.value)}
                            data-testid={`editor-input-${sec.key}-${f.key}`}
                            className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <button type="button" onClick={() => saveSection(sec.key)} disabled={savingSec === sec.key}
                    data-testid={`editor-save-${sec.key}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-gold-deep px-4 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
                    {savingSec === sec.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save {sec.label}
                  </button>
                  {savedSec === sec.key && <span className="text-sm text-sage-deep" data-testid={`editor-saved-${sec.key}`}>Saved</span>}
                </div>
              </section>
            ))}

            <section className="rounded-2xl bg-ivory-50 p-5 shadow-sm ring-1 ring-ivory-400/60 sm:p-6" data-testid="editor-welcome-test">
              <h2 className="font-display text-xl text-ink">Welcome email</h2>
              <p className="text-xs text-ink-muted">Sent once, automatically, the first time a reader enters their email. Send yourself a preview here.</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com"
                  data-testid="welcome-test-email"
                  className="min-w-[220px] flex-1 rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                <button type="button" disabled={testSending}
                  onClick={async () => {
                    setTestMsg("");
                    if (!testEmail.includes("@")) { setTestMsg("Enter a valid email."); return; }
                    setTestSending(true);
                    try {
                      await axios.post(`${API}/admin/welcome-email/test`, { email: testEmail.trim(), origin_url: window.location.origin }, auth);
                      setTestMsg(`Sent to ${testEmail.trim()} — check the inbox.`);
                    } catch (e) {
                      setTestMsg(e.response?.data?.detail || "Could not send the test.");
                    } finally { setTestSending(false); }
                  }}
                  data-testid="welcome-test-send"
                  className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
                  {testSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send me a test
                </button>
              </div>
              {testMsg && <p className="mt-2 text-sm text-sage-deep" data-testid="welcome-test-msg">{testMsg}</p>}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
